import { useCallback, useEffect, useRef, useState } from 'react';

import { debounce } from 'lodash';
import { get } from 'helpers/api';
import { RECORD_CHANGED_EVENT } from 'helpers/record_changed';
import { getDefaults, hasFilters, readDeepLinkedFilters, serializeFilters } from './filters/helpers';

import type { RecordChange } from 'helpers/record_changed';
import type { Activity, PaginationData } from './types';
import type { ApiFilters, Filters } from './filters/types';

export type Paths = {
  index?: string;
}

// For reuse in the event listener setup
const refreshEventName = 'refreshActivities';
const defaultFilters = getDefaults();

const byServerOrder = (a: Activity, b: Activity) => {
  const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
  return dateDiff !== 0 ? dateDiff : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
};

export function refreshActivities() {
  window.dispatchEvent(new CustomEvent(refreshEventName));
}

export function useActivitiesState(
  { activities: externalActivities, paginate, paths, showPinned, seedFiltersFromUrl, onPageDataChange, onChange }: {
    activities?: Activity[],
    paths?: Paths,
    paginate?: boolean,
    showPinned?: boolean,
    seedFiltersFromUrl?: boolean,
    onPageDataChange?: (pageData: PaginationData) => void,
    onChange?: (_: Activity[]) => void
  } = {}
) {
  const [activities, setActivities] = useState(externalActivities || []);
  const [pinnedActivities, setPinnedActivities] = useState<Activity[]>([]);
  const [loaded, setLoaded] = useState(!!externalActivities || !paths?.index);
  const [loading, setLoading] = useState(!loaded);
  const [filters, _setFilters] = useState<Filters>(
    () => (seedFiltersFromUrl && readDeepLinkedFilters()) || defaultFilters
  );
  const [pageData, setPageData] = useState<PaginationData | null>(null);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  function handleResponse(response) {
    setLoading(false);
    setActivities(response.activities);
    setPinnedActivities(response.pinnedActivities || []);

    if (response.meta) {
      setPageData(response.meta);

      if (onPageDataChange) {
        onPageDataChange(response.meta as PaginationData);
      }
    }
  }

  const reloadActivities = async () => {
    if (paths?.index) {
      setLoading(true);
      const response = showPinned
        ? await get(paths.index, { params: { showPinned: true } })
        : await get(paths.index);
      handleResponse(response);
      setLoaded(true);
    }
  };

  useEffect(() => {
    (async () => {
      if (paths?.index && !loaded) {
        if (hasFilters(filters)) {
          setLoading(true);
          await searchActivities(paths.index, serializeFilters(filters));
          setLoaded(true);
        } else {
          reloadActivities();
        }
      }
    })();

    // Through searchActivities rather than reloadActivities: it keeps the rows on screen and honours
    // the filters and page the user is on
    const onRecordChanged = (event: Event) => {
      const change = (event as CustomEvent<RecordChange>).detail;
      if (change?.kind !== 'activity_created' || !paths?.index) return;

      searchActivities(paths.index, serializeFilters(filtersRef.current));
    };

    window.addEventListener(refreshEventName, reloadActivities);
    window.addEventListener(RECORD_CHANGED_EVENT, onRecordChanged);

    return () => {
      window.removeEventListener(refreshEventName, reloadActivities);
      window.removeEventListener(RECORD_CHANGED_EVENT, onRecordChanged);
    };
  }, [paths?.index]);

  useEffect(() => {
    // Currently, the activities component handles editing and deletion, but not adding an activity.
    // On deals, activity creation is handled externally, and the activities are also stored in the
    // deal's state. While we need to be able to ingest those added activities here, we need to
    // avoid infinite rendering loops triggered by this component exposing a change to the deal,
    // which updates it's store, which feeds that new object back in here, etc. Since the only thing
    // that can happen externally is creation, just checking length for a change is sufficient.
    // NOTE: This is weird but should be able to be cleaned up when activity creating moves out of
    //       deal code and into this standalone activity module
    if (externalActivities && externalActivities.length !== activities.length) {
      setActivities(externalActivities);
    }
  }, [externalActivities]);

  function changeActivities(changeFunction) {
    setActivities((activities) => {
      const newActivities = changeFunction(activities);

      if (onChange) {
        onChange(newActivities);
      }

      return newActivities;
    });
  }

  const addActivity = useCallback((savedActivity) => {
    changeActivities(activities => [savedActivity, ...activities]);
  }, []);

  const replaceActivity = useCallback((savedActivity) => {
    const replace = (list: Activity[]) => {
      return list.map(activity => activity.id === savedActivity.id ? savedActivity : activity);
    };

    changeActivities(replace);
    setPinnedActivities(replace);
  }, []);

  const removeActivity = useCallback((removedActivity) => {
    const without = (list: Activity[]) => list.filter(activity => activity.id !== removedActivity.id);

    changeActivities(without);
    setPinnedActivities(without);
  }, []);

  const adjustMainCount = (delta: number) => {
    setPageData((current) => {
      if (!current) return current;

      const totalCount = Math.max(0, current.totalCount + delta);

      return {
        ...current,
        itemCount: Math.max(0, current.itemCount + delta),
        totalCount,
        totalPages: Math.max(1, Math.ceil(totalCount / current.pageSize)),
      };
    });
  };

  const changePinned = useCallback((savedActivity: Activity, pinned: boolean) => {
    const withoutSaved = (list: Activity[]) => list.filter(a => a.id !== savedActivity.id);
    const insertSorted = (list: Activity[]) => [savedActivity, ...withoutSaved(list)].sort(byServerOrder);

    if (pinned) {
      changeActivities(withoutSaved);
      setPinnedActivities(insertSorted);
    } else {
      setPinnedActivities(withoutSaved);
      changeActivities(insertSorted);
    }

    adjustMainCount(pinned ? -1 : 1);
  }, []);

  const setFilters = useCallback(async (filters: Filters, debounce = false) => {
    _setFilters(filters);

    if (paths?.index) {
      setLoading(true);
      const performSearch = debounce ? debouncedSearch : searchActivities;
      performSearch(paths.index, serializeFilters(filters));
    }
  }, [paths?.index]);

  const changeFilters = (changes: Partial<Filters>, debounce = false) => {
    const changedFilters = { ...filters, page: 1, ...changes };
    setFilters(changedFilters, debounce);
  };

  const debouncedSearch = debounce(searchActivities, 500);
  async function searchActivities(path: string, filters: ApiFilters) {
    if (!paginate) {
      delete filters.page;
    }

    if (showPinned) {
      filters.showPinned = true;
    }

    const response = await get(path, { params: filters });
    handleResponse(response);
  }

  return {
    activities,
    pinnedActivities,
    loading,
    pageData,
    addActivity,
    replaceActivity,
    removeActivity,
    changePinned,
    filters,
    changeFilters,
  };
}
